import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { LocationViewModel } from "../../../../shared/models/locations/LocationViewModel.model";
import { LocationsService } from "../../../shared/locations.service";
import { FileUploadService } from "../../../../shared/services/files/fileUpload.service";
import { FileUploadComponent } from "../../../../shared/components/file-upload/file-upload.component";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "app-location-files-tab",
  templateUrl: "./location-files-tab.component.html",
  styleUrls: ["./location-files-tab.component.css"],
  providers: [FileUploadComponent],
})
export class LocationFilesTabComponent implements OnInit, OnChanges {
  @Input() location: LocationViewModel;
  @Input() isEditMode: boolean;
  @Output() hasChanges = new EventEmitter<boolean>();

  files: any[] = [];
  loadingFiles: boolean = false;
  uploadingFiles: boolean = false;
  showPreviewModal: boolean = false;
  previewFile: any = null;
  @ViewChild("fileInput", { static: false }) fileInput: ElementRef;

  constructor(
    private locationsService: LocationsService,
    private fileUploadService: FileUploadService,
    private fileUploadComponent: FileUploadComponent,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    // Set allowed file extensions including JPEG
    this.fileUploadComponent.fileExt = "JPG, JPEG, PDF, TIFF";
    
    if (this.location?.id) {
      this.loadFiles();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["location"] && !changes["location"].firstChange && this.location?.id) {
      this.loadFiles();
    }
  }

  loadFiles() {
    if (!this.location?.id) {
      return;
    }

    this.loadingFiles = true;
    this.locationsService.getLocationFiles(this.location.id).subscribe({
      next: (files) => {
        this.files = files || [];
        this.loadingFiles = false;
      },
      error: (error) => {
        this.toastr.error("Error loading files", "Error");
        this.loadingFiles = false;
        this.files = [];
      },
    });
  }

  fileSelected(event: any) {
    if (!this.isEditMode) {
      return;
    }

    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    if (!this.fileUploadComponent.isValidFileExtension(files)) {
      this.toastr.warning(
        "Check the file extension. You have to upload .jpg, .jpeg, .pdf, .tiff extensions",
        "Alert!"
      );
      return;
    }

    this.uploadFiles(Array.from(files));
  }

  uploadFiles(fileList: File[]) {
    if (!this.location?.id || fileList.length === 0) {
      return;
    }

    this.uploadingFiles = true;
    this.fileUploadService.uploadFiles(fileList, "location").subscribe({
      next: (results: any[]) => {
        if (results && results.length > 0) {
          const filePromises = results.map((result) => {
            const locationFile = {
              locationId: this.location.id,
              originalName: result.originalName || result.name,
              name: result.name,
              type: result.type,
              blobName: result.path,
            };

            // Save file reference to database
            return this.locationsService.createLocationFile(locationFile).toPromise();
          });

          Promise.all(filePromises)
            .then(() => {
              this.loadFiles();
              this.hasChanges.emit(true);
              this.toastr.success("Files uploaded successfully", "Success");
              this.uploadingFiles = false;
              if (this.fileInput) {
                this.fileInput.nativeElement.value = "";
              }
            })
            .catch((error) => {
              this.toastr.error("Error saving file references", "Error");
              this.uploadingFiles = false;
            });
        } else {
          this.uploadingFiles = false;
        }
      },
      error: (error) => {
        this.toastr.error("Error uploading files", "Error");
        this.uploadingFiles = false;
      },
    });
  }

  downloadFile(file: any) {
    const url = this.fileUploadService.getAzureFileUrl("location", file.name);
    window.open(url, "_blank");
  }

  deleteFile(fileId: string) {
    if (!this.isEditMode) {
      return;
    }

    if (!confirm("Are you sure you want to delete this file?")) {
      return;
    }

    this.locationsService.deleteLocationFile(fileId).subscribe({
      next: () => {
        this.loadFiles();
        this.hasChanges.emit(true);
        this.toastr.success("File deleted successfully", "Success");
      },
      error: (error) => {
        this.toastr.error("Error deleting file", "Error");
      },
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) {
      return "";
    }
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  // File preview methods
  isImageFile(fileName: string): boolean {
    if (!fileName) return false;
    const extension = fileName.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'tif'].includes(extension || '');
  }

  isPdfFile(fileName: string): boolean {
    if (!fileName) return false;
    const extension = fileName.toLowerCase().split('.').pop();
    return extension === 'pdf';
  }

  getFileExtension(fileName: string): string {
    if (!fileName) return '';
    return fileName.toLowerCase().split('.').pop()?.toUpperCase() || '';
  }

  getFileIcon(fileName: string): string {
    if (!fileName) return 'fa-file';
    const extension = fileName.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf-o';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
      case 'tiff':
      case 'tif':
        return 'fa-file-image-o';
      case 'doc':
      case 'docx':
        return 'fa-file-word-o';
      case 'xls':
      case 'xlsx':
        return 'fa-file-excel-o';
      case 'txt':
        return 'fa-file-text-o';
      default:
        return 'fa-file-o';
    }
  }

  getImageUrl(fileName: string): string {
    return this.fileUploadService.getAzureFileUrl('location', fileName);
  }

  getPdfUrl(fileName: string): string {
    return this.fileUploadService.getAzureFileUrl('location', fileName);
  }

  openFilePreview(file: any) {
    this.previewFile = file;
    this.showPreviewModal = true;
  }

  closePreviewModal() {
    this.showPreviewModal = false;
    this.previewFile = null;
  }
}

